<script setup lang="ts">
import { computed } from "vue";

type SectionKey =
  | "time"
  | "roza-countdown"
  | "roza"
  | "zakat"
  | "dua"
  | "lailat"
  | "calendar"
  | "fitrana"
  | "zikr"
  | "asma";

const props = withDefaults(
  defineProps<{
    current?: SectionKey;
    showCityLinks?: boolean;
    selectedCityKey?: string | null;
  }>(),
  {
    showCityLinks: false,
    selectedCityKey: null,
  },
);

const sectionLinks = [
  {
    key: "time",
    to: "/",
    anchor: "Sehri & Iftar time today (Pakistan)",
    context: "Live sehri aur iftar times with countdown",
  },
  {
    key: "roza-countdown",
    to: "/roza-countdown/",
    anchor: "Roza countdown 2026 Pakistan",
    context: "Live timer for Ramzan start with daily date context",
  },
  {
    key: "calendar",
    to: "/calendar/",
    anchor: "Ramzan 2026 calendar Pakistan",
    context: "Complete month-wise sehri and iftar schedule",
  },
  {
    key: "roza",
    to: "/roza/",
    anchor: "Roza ahkaam aur niyat guide",
    context: "Basic fasting rules, qaza and kaffarah guidance",
  },
  {
    key: "zakat",
    to: "/zakat/",
    anchor: "Simple zakat calculator",
    context: "2.5% zakat estimate with nisab check in Roman Urdu",
  },
  {
    key: "dua",
    to: "/dua/",
    anchor: "Ramzan ki duain",
    context: "Sehri, iftar and laylatul qadr duas",
  },
  {
    key: "lailat",
    to: "/shab-e-qadr/",
    anchor: "Lailatul Qadr guide",
    context: "Fazilat, odd nights and ibadat points",
  },
  {
    key: "fitrana",
    to: "/fitrana/",
    anchor: "Fitrana amount and FAQs",
    context: "Per person amount, calculator and common questions",
  },
  {
    key: "zikr",
    to: "/zikr/",
    anchor: "Zikr counter and tasbih tracker",
    context: "Simple digital counter for daily dhikr and adhkar",
  },
  {
    key: "asma",
    to: "/asma/",
    anchor: "Asma Ul Husna reference",
    context: "99 Names of Allah with meanings and reflections",
  },
] as const;

const cityLinks = [
  { to: "/islamabad/", label: "Islamabad roza time" },
  { to: "/lahore/", label: "Lahore roza time" },
  { to: "/karachi/", label: "Karachi roza time" },
  { to: "/multan/", label: "Multan roza time" },
  { to: "/quetta/", label: "Quetta roza time" },
  { to: "/gilgit/", label: "Gilgit roza time" },
] as const;

const relatedLinks = computed(() =>
  sectionLinks.filter((link) => link.key !== props.current),
);

const route = useRoute();

const normalizePath = (path: string) => path.replace(/\/+$/, "") || "/";

const activeCityPath = computed(() => {
  const currentPath = normalizePath(route.path);
  const active = cityLinks.find((city) => normalizePath(city.to) === currentPath);
  if (active) return active.to;

  if (props.selectedCityKey) {
    const selectedPath = normalizePath(`/${props.selectedCityKey}/`);
    const selected = cityLinks.find(
      (city) => normalizePath(city.to) === selectedPath,
    );
    if (selected) return selected.to;
  }

  return null;
});
</script>

<template>
  <aside
    class="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/40 p-4 space-y-3"
    aria-label="Related pages"
  >
    <h2 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
      Related pages
    </h2>

    <ul class="space-y-2">
      <li v-for="link in relatedLinks" :key="link.to">
        <NuxtLink
          :to="link.to"
          class="text-sm font-medium text-emerald-700 dark:text-emerald-300 hover:underline underline-offset-2"
        >
          {{ link.anchor }}
        </NuxtLink>
        <p class="text-xs text-gray-600 dark:text-gray-400">
          {{ link.context }}
        </p>
      </li>
    </ul>

    <div v-if="showCityLinks" class="pt-2 border-t border-gray-200 dark:border-gray-700">
      <h3 class="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-2">
        City timing pages
      </h3>
      <div class="flex flex-wrap gap-2">
        <template v-for="city in cityLinks" :key="city.to">
          <button
            v-if="activeCityPath === city.to"
            type="button"
            disabled
            aria-current="page"
            class="rounded-full border px-2.5 py-1 text-xs transition-colors border-gray-300 bg-gray-200 text-gray-600 cursor-not-allowed dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
          >
            {{ city.label }}
          </button>

          <NuxtLink
            v-else
            :to="city.to"
            class="rounded-full border px-2.5 py-1 text-xs transition-colors border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800"
          >
            {{ city.label }}
          </NuxtLink>
        </template>
      </div>
    </div>
  </aside>
</template>

